
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const units = [
  { id: 'pa', name: 'Pascal (Pa)' },
  { id: 'kpa', name: 'Kilopascal (kPa)' },
  { id: 'mpa', name: 'Megapascal (MPa)' },
  { id: 'bar', name: 'Bar' },
  { id: 'psi', name: 'PSI' },
  { id: 'ksi', name: 'KSI' },
  { id: 'atm', name: 'Atmosphere (atm)' },
  { id: 'torr', name: 'Torr' },
  { id: 'mmhg', name: 'mmHg' },
  { id: 'cmh2o', name: 'cmH₂O' },
  { id: 'inh2o', name: 'inH₂O' },
  { id: 'inhg', name: 'inHg' },
];

// Conversion rates to Pascal
const conversionRates: Record<string, number> = {
  'pa': 1,
  'kpa': 1000,
  'mpa': 1000000,
  'bar': 100000,
  'psi': 6894.75729,
  'ksi': 6894757.29,
  'atm': 101325,
  'torr': 133.3223684,
  'mmhg': 133.3223684,
  'cmh2o': 98.0665,
  'inh2o': 249.0889,
  'inhg': 3386.389,
};

const PressureConverter = () => {
  const [fromValue, setFromValue] = useState<string>('1');
  const [fromUnit, setFromUnit] = useState<string>('bar');
  const [toUnit, setToUnit] = useState<string>('psi');
  const [toValue, setToValue] = useState<string>('');

  useEffect(() => {
    convert();
  }, [fromValue, fromUnit, toUnit]);

  const convert = () => {
    try {
      const inputValue = parseFloat(fromValue);
      if (!isNaN(inputValue)) {
        // Convert to base unit (Pascal)
        const baseValue = inputValue * conversionRates[fromUnit];
        // Convert from base unit to target unit
        const result = baseValue / conversionRates[toUnit];
        setToValue(result.toFixed(10).replace(/\.?0+$/, ""));
      } else {
        setToValue('');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setToValue('Error');
    }
  };

  const handleFromValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromValue(e.target.value);
  };

  const handleFromUnitChange = (value: string) => {
    setFromUnit(value);
  };

  const handleToUnitChange = (value: string) => {
    setToUnit(value);
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Pressure Converter</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">From:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="number" 
                value={fromValue} 
                onChange={handleFromValueChange} 
                className="col-span-2"
              />
              <Select value={fromUnit} onValueChange={handleFromUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-center">
            <button 
              onClick={swapUnits}
              className="p-2 rounded-full hover:bg-muted"
            >
              ⇅
            </button>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">To:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="text" 
                value={toValue} 
                readOnly 
                className="col-span-2"
              />
              <Select value={toUnit} onValueChange={handleToUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PressureConverter;
